/**
 * Telemetry emitter — the single entry point for all in-app markers.
 *
 * Behavior depends on environment:
 *   - DEV: pretty-prints to console (collapsed group per category) so the
 *     developer can scrub the entire user flow in DevTools.
 *   - PROD: silently appends to an in-memory ring buffer. Once we have a
 *     backend (`/api/telemetry` per docs/15) we will batch-send the buffer
 *     every N seconds. Until then the buffer is inspectable from the
 *     browser console as `window.__SAI_TELEMETRY__`.
 *
 * Privacy:
 *   - This module NEVER auto-sends events to a remote server. The future
 *     send step will check `consent.analytics === true` before any fetch.
 *   - The `__SAI_TELEMETRY__` global is for debugging only and exposes
 *     only the data the caller chose to pass — see `events.ts` for the
 *     typed catalog that excludes raw PII.
 */
import { shouldDropTelemetry } from './consent.js';
import { EVENT_CATEGORY, type EventName, type EventPayloads } from './events.js';
import { enqueueEventForFlush } from './flush.js';

export interface TelemetryRecord<N extends EventName = EventName> {
  readonly name: N;
  readonly category: string;
  readonly payload: EventPayloads[N];
  /** ms since the page loaded — relative time is easier to read in logs. */
  readonly relativeMs: number;
  /** Absolute ms epoch — for ordering across reloads later. */
  readonly absoluteMs: number;
}

const RING_BUFFER_MAX = 1000;
const buffer: TelemetryRecord[] = [];
// `performance` is universally available in modern browsers, vitest's jsdom,
// and Node. Captured at module init so all `relativeMs` values share the
// same origin even if the module is re-entered.
const PAGE_LOAD_MS = performance.now();

/** Strict-typed event emitter. */
export function emit<N extends EventName>(name: N, payload: EventPayloads[N]): void {
  // GDPR consent gate (doc 15). Если игрок явно отказал — событие
  // вообще не запоминается. Если 'unknown' или 'granted' — буферизуем
  // локально (remote отправка будет gated на 'granted' когда backend
  // появится).
  if (shouldDropTelemetry()) return;

  const record: TelemetryRecord<N> = {
    name,
    category: EVENT_CATEGORY[name],
    payload,
    relativeMs: Math.round(performance.now() - PAGE_LOAD_MS),
    absoluteMs: Date.now(),
  };

  // Ring buffer (drop oldest when full).
  if (buffer.length >= RING_BUFFER_MAX) buffer.shift();
  buffer.push(record);

  // Backend flush queue. `flush.ts` сам gate'нет на consent==='granted'
  // и наличие VITE_API_BASE env var'а — здесь просто кладём в очередь.
  enqueueEventForFlush({
    name: record.name,
    category: record.category,
    payload: record.payload,
    occurred_at_ms: record.absoluteMs,
  });

  // Dev console pretty print — color per category so the flow is scannable.
  if (import.meta.env.DEV) {
    const color = COLOR_BY_CATEGORY[record.category] ?? '#888';
    // eslint-disable-next-line no-console
    console.log(
      `%c⟡ ${record.name}%c +${record.relativeMs.toString()}ms`,
      `color:${color};font-weight:600`,
      'color:#888;font-weight:400',
      record.payload,
    );
  }
}

/** Inspect the buffer from the browser console (DEV and PROD). */
export function getTelemetryBuffer(): readonly TelemetryRecord[] {
  return buffer.slice();
}

/** Wipe the buffer — useful in tests and for "fresh recording" flows. */
export function clearTelemetryBuffer(): void {
  buffer.length = 0;
}

const COLOR_BY_CATEGORY: Readonly<Record<string, string>> = {
  session: '#7AA864', // green
  game: '#F2C95C', // gold
  sound: '#A4673A', // brown
  sensory: '#C0A8E0', // lavender
  onboarding: '#7AB8A0', // moss
  payment: '#E8A047', // amber
  profile: '#E8967A', // clay
  consent: '#A0B8D0', // info blue
  perf: '#888888',
  error: '#E07A6A', // coral
};

// Expose for in-browser inspection on both dev and prod (read-only).
if (typeof window !== 'undefined') {
  Object.defineProperty(window, '__SAI_TELEMETRY__', {
    configurable: true,
    get: () => getTelemetryBuffer(),
  });
}
